<template>
  <v-container fluid class="mx-4 px-8">
    <v-row class="mt-2">
      <v-col cols="2">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.applicationNumber]"
          maxlength="15"
          label="Application ID"
        ></v-text-field>
      </v-col>
      <v-col cols="2">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Programme"
        ></v-autocomplete>
      </v-col>
      <v-col cols="2" v-if="showPartner">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Partner"
        ></v-autocomplete>
      </v-col>
      <v-col cols="2">
        <v-select
          v-model="form.option_selected"
          :items="payment_types"
          outlined
          dense
          hide-details="auto"
          label="Payment Type"
        ></v-select>
      </v-col>
      <v-col cols="12" md="5" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="getPaymentList"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!paymentListData.length"
          class="accent white--text ml-2"
          @click="download"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
        <v-btn class="accent white--text ml-2" @click="showUploadDialog = true">
          <v-icon>mdi-upload</v-icon> Upload Loan Payments
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-3">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="businessReportHeaders"
            :items="paymentListData"
            item-key="stn"
            fixed-header
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.action_business`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="
                  edit(
                    item.applicant_detail_id
                      ? item.applicant_detail_id
                      : item.id,
                    item.program_id,
                  )
                "
              >
                mdi-pencil
              </v-icon>
            </template>
            <template v-slot:[`item.set_payment`]="{ item }">
              <v-icon
                class="mr-2"
                color="accent"
                @click="navigateToPaymentOptions(item)"
              >
                mdi-dots-horizontal
              </v-icon>
            </template>
            <template v-slot:[`item.loan_utr`]="{ item }">
              <v-icon
                v-if="item.option_selected == 'Loan'"
                class="mr-2"
                color="accent"
                @click="
                  getApprovedPaymentStatus(
                    item.applicant_detail_id
                      ? item.applicant_detail_id
                      : item.id,
                  )
                "
              >
                mdi-dots-horizontal
              </v-icon>
              <div v-else>-</div>
            </template>
            <template v-slot:[`item.transaction_id`]="{ item }">
              {{
                item.method == "Installment"
                  ? "-"
                  : item.method == "Loan"
                  ? item.loan_utr
                  : item.transaction_id
              }}
            </template>
            <template v-slot:[`item.transaction_date`]="{ item }">
              {{
                item.method == "Installment"
                  ? "-"
                  : formatDate(item.transaction_date)
              }}
            </template>
            <template v-slot:[`item.approved_by`]="{ item }">
              {{ item.method != "Loan" ? "-" : getUserName(item.approved_by) }}
            </template>
            <template v-slot:[`item.partner_id`]="{ item }">
              {{ getPartnerName(item.partner_id) }}
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              {{
                item.method == "Installment"
                  ? item.installment_amount_received
                  : item.amount
              }}
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-icon
                v-if="item.method == 'Installment'"
                small
                class="mr-2"
                color="accent"
                @click="viewInstallments(item.applicant_detail_id, item.id)"
              >
                mdi-eye
              </v-icon>
              <div v-else>-</div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" fullscreen persistent no-click-animation>
      <formDialog
        v-if="dialog"
        @update="getPaymentList, (dialog = false)"
        :applicant="applicantDetail"
        :qualifications="qualifications"
      />
    </v-dialog>

    <v-dialog v-model="showUploadDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Upload Loan Payments CSV
          <v-spacer></v-spacer>
          <v-btn small icon @click="closeUploadDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <div class="mb-4">
            <h4 class="primary--text mb-2">CSV Format Instructions</h4>
            <v-sheet class="pa-3 grey lighten-4 rounded">
              <p class="font-weight-medium mb-2">
                Required Columns (in order):
              </p>
              <ol class="pl-3">
                <li>
                  <strong>Loan Provider</strong> - Name of the loan provider
                  (required, free text)
                </li>
                <li><strong>Loan UTR</strong> - Loan UTR number (required)</li>
                <li>
                  <strong>Payment Date</strong> - Date in DD-MM-YYYY format,
                  within the last 2 days (required)
                </li>
                <li>
                  <strong>Amount</strong> - Loan amount, cannot exceed the
                  balance due (required)
                </li>
              </ol>

              <div class="pt-2 d-flex justify-center">
                <v-btn color="primary" @click="downloadTemplate">
                  <v-icon left>mdi-download</v-icon>
                  Download Template
                </v-btn>
              </div>

              <v-alert type="info" dense class="mt-3 mb-0">
                <small>
                  <strong>Note:</strong>
                  The template is pre-filled with students who have a Loan
                  payment pending (based on the current search filters
                  above), along with their Employee ID, Name, Partner,
                  Programme and Balance Due for reference. Amount defaults to
                  the balance due but can be edited. If any errors occur
                  during upload, an error CSV is automatically downloaded -
                  correct the rows and re-upload.
                </small>
              </v-alert>
            </v-sheet>
          </div>

          <v-file-input
            ref="fileInput"
            v-model="csvFile"
            accept=".csv"
            label="Select CSV File"
            outlined
            dense
            prepend-icon="mdi-file-document"
            :loading="uploadLoading"
            :disabled="uploadLoading"
            show-size
            counter
          />
        </v-card-text>

        <v-card-actions class="pt-0 pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn @click="closeUploadDialog" :disabled="uploadLoading">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="uploadLoanPaymentsCsv"
            :loading="uploadLoading"
            :disabled="!csvFile"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="uploadErrorDialog" width="450" persistent>
      <uploadError
        v-if="uploadErrorDialog"
        :errorCsvBlob="errorCsvBlob"
        @downloaded="onErrorCsvDownloaded"
        csvTitle="Loan Payments Upload"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import formDialog from "@/components/applicant/form.vue";
import uploadError from "@/components/uploadError.vue";
import { applicantPayload } from "@/helpers/payload";
import { rules } from "@/helpers/rules";
import env from "@/helpers/env";
import {
  businessReportHeaders,
  installmentsHeaders,
} from "@/constants/headers";

export default {
  components: {
    formDialog,
    uploadError,
  },
  data() {
    return {
      rules,
      form: {},
      installmentsHeaders,
      businessReportHeaders,
      dialog: false,
      applicantDetail: {},
      qualifications: [],
      programs: [],
      partners: [],
      users: [],
      paymentListData: [],
      installmentsList: [],
      installmentsDialog: false,
      loading: false,
      payment_types: [
        {
          value: "Direct",
          text: "Self Payment",
        },
        {
          value: "Installment",
          text: "Installment",
        },
        {
          value: "Loan",
          text: "Loan",
        },
      ],
      env,
      showUploadDialog: false,
      csvFile: null,
      uploadLoading: false,
      uploadErrorDialog: false,
      errorCsvBlob: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    showPartner() {
      return (
        this.currentUser.partner_id == 1 ||
        (this.partners.length > 1 && this.currentUser.partner_level != "S")
      );
    },
    formatDate() {
      return (date) => {
        if (!date) return "";
        const [year, month, day] = date.split("-");
        return `${day}-${month}-${year}`;
      };
    },
  },
  async created() {
    if (this.currentUser.partner_payment_required)
      this.$router.push("/bulk_payments");

    this.$store.commit("setBreadcrumbs", [{ text: "Set Payment Options" }]);
    this.init();
    this.getPaymentList();
  },
  methods: {
    init() {
      Promise.all([this.getPrograms(), this.getPartners(), this.getUsers()]);
    },
    async viewInstallments(applicant_detail_id, payment_resolution_id) {
      const res = await this.$axios.get(
        `/invoices/?applicant_detail_id=${applicant_detail_id}&payment_resolution_id=${payment_resolution_id}`,
      );
      const payments = res.data.data.sort((a, b) => a.id - b.id);

      this.installmentsList = [...payments];
      this.installmentsDialog = true;
    },
    async edit(applicant_detail_id, program_id) {
      const [program, applicant] = await Promise.all([
        this.$axios.get(`/programs/${program_id}`),
        this.$axios.get(`/applicant_details/${applicant_detail_id}`),
      ]);

      const applicantData = applicant.data.data;

      this.qualifications = program.data.data.qualifications;
      this.applicantDetail = applicantPayload(
        this.qualifications,
        applicantData,
      );

      this.dialog = true;
    },
    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      return Object.entries(obj)
        .filter(
          ([_, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&");
    },

    getApprovedPaymentStatus(applicant_detail_id) {
      this.$router.push({
        path: "payment-options",
        query: { id: applicant_detail_id },
      });
    },
    async getPaymentList() {
      this.loading = true;
      try {
        if (this.currentUser.role !== "Coordinator") {
          this.form.partner_id = this.currentUser.partner_id;
        }
        const queryString = this.objectToQueryParams(this.form);
        const res = await this.$axios.get(
          `/student_admit_plans?${queryString}&!plan_amount_balance=0`,
        );
        this.paymentListData = res.data.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async getPrograms() {
      try {
        const url =
          this.currentUser.role === "Partner" ||
          this.currentUser.role === "Business" ||
          this.currentUser.role === "Business Reviewer"
            ? `/get_partner_programs/${this.currentUser.partner_id}`
            : "/programs";
        const response = await this.$axios.get(url);
        this.programs = response.data.data;
        if (this.programs.length === 1) {
          this.form.program_id = this.programs[0].id;
        }
      } catch (error) {
        console.log(error);
      }
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getPartnerName(id) {
      const partner = this.partners.find((partner) => partner.id === id);
      return partner ? partner.name : "";
    },
    async getUsers() {
      try {
        const response = await this.$axios.get("/users");
        this.users = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getUserName(userId) {
      const user = this.users.find((user) => user.id === userId);
      return user ? user.first_name + " " + user.last_name : "";
    },
    navigateToPaymentOptions(item) {
      // this.$router.push({ name: "Set Payment Options", params: { id: item.id } });
      window.open(
        `${this.env.application_url}/payment-options?id=${item.applicant_detail_id}&partner_payment=true`,
        "_self",
      );
    },
    async download() {
      if (this.currentUser.role !== "Coordinator") {
        this.form.partner_id = this.currentUser.partner_id;
      }
      const queryString = this.objectToQueryParams(this.form);
      this.$axios
        .get(`/download_balance_fee_due?${queryString}`, {
          params: {
            ...this.form,
          },
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `set_payment_options.csv`);
          document.body.appendChild(link);
          link.click();
        });
    },
    closeUploadDialog() {
      this.showUploadDialog = false;
      this.csvFile = null;
    },
    async downloadTemplate() {
      const queryString = this.objectToQueryParams(this.form);
      const res = await this.$axios.get(
        `/download_loan_payment_template?${queryString}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "loan_payments_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async uploadLoanPaymentsCsv() {
      if (!this.csvFile) return;
      this.uploadLoading = true;

      const formData = new FormData();
      formData.append("file", this.csvFile);

      try {
        const response = await this.$axios.post(
          "/upload_loan_payments",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (response.status === 201) {
          this.errorCsvBlob = new Blob([response.data]);
          this.uploadErrorDialog = true;
          this.closeUploadDialog();
          return;
        }

        this.$store.commit("showSnackbar", {
          message: "Loan payments uploaded successfully",
          color: "success",
        });
        this.closeUploadDialog();
        await this.getPaymentList();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.uploadLoading = false;
      }
    },
    onErrorCsvDownloaded() {
      this.uploadErrorDialog = false;
      this.errorCsvBlob = null;
      this.getPaymentList();
    },
  },
};
</script>

<style scoped></style>
